import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import { logPptoDebug } from "scmp-sdk";

import { PageType } from "shared/lib/page-type";

import { useArticlePianoPaywallContext } from "scmp-app/components/article/article-piano-paywall/contexts";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useRosettaPianoExecution } from "scmp-app/lib/rosetta/hooks/rosetta-piano-execution";

/**
 * Whether Piano will render an inline/modal wall for the current article page.
 *
 * Runtime-authoritative: reads Piano's own decision from
 * `articlePianoPaywallStateMap[entityId]` (the per-article map fed by the Piano
 * `experienceExecute` callback and the `rendered/paywall` template channel)
 * rather than classifying article tags. Wall type is irrelevant (meter, adblock,
 * strategy, program — inline or modal); any active wall resolves `true`
 * (design.md D6 revised).
 *
 * Contract:
 * - `false` on non-article pages (never `undefined` there).
 * - `true` when Piano is showing/offering a wall for this article.
 * - `false` when no wall is required, or when Piano execution is skipped
 *   (bots/Lighthouse — no wall will render, so resolve rather than hold).
 * - `undefined` only while the current article page's decision is pending
 *   (fail-closed).
 *
 * Never use `!signal`-style truthy logic on this value; compare with
 * `=== false` / `!== false` (design.md D5).
 */
export const useIsInlinePaywallArticle = (options: {
  currentArticleId: string | undefined;
  currentPageType: PageType;
}): boolean | undefined => {
  const { currentArticleId, currentPageType } = options;

  const router = useRouter();
  const { articlePianoPaywallStateMap } = useArticlePianoPaywallContext();
  const { navigatorInfo, navigatorInfoLoaded } = useAtomValue(appAtom);
  const { isRosettaPianoExecutionReady, shouldSkipPianoExecution } = useRosettaPianoExecution(
    navigatorInfo,
    navigatorInfoLoaded,
  );

  // The pageType/article routes inject `query.entityId` on both SSR and client;
  // `currentArticleId` (from useCurrentArticleIds) is only a fallback.
  const entityId =
    typeof router.query.entityId === "string" ? router.query.entityId : currentArticleId;
  const paywallState = entityId ? articlePianoPaywallStateMap?.[entityId] : undefined;

  const lastLoggedReference = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (currentPageType !== PageType.Article) return;

    const signature = `${entityId}|${paywallState}|${isRosettaPianoExecutionReady}|${shouldSkipPianoExecution}`;
    if (lastLoggedReference.current === signature) return;
    lastLoggedReference.current = signature;

    logPptoDebug(
      `paywall decision — entityId=${entityId} state=${paywallState} pianoReady=${isRosettaPianoExecutionReady} skip=${shouldSkipPianoExecution}`,
    );
  }, [
    currentPageType,
    entityId,
    isRosettaPianoExecutionReady,
    paywallState,
    shouldSkipPianoExecution,
  ]);

  // Plus inline-paywall pages (PageType.Plus + isPlusInlinePaywall query param)
  // are intentionally NOT covered per the spec contract; noted as an extension
  // point for a future Plus-page signal.
  if (currentPageType !== PageType.Article) return false;

  if (!entityId) return undefined;

  if (
    paywallState?.startsWith("shown") ||
    paywallState === "requiresInlinePaywall" ||
    paywallState === "requiresModalPaywall"
  ) {
    return true;
  }

  if (paywallState === "paywallNotRequired") return false;

  // Piano execution skipped (bots/Lighthouse): no wall will render — resolve, don't hold.
  if (isRosettaPianoExecutionReady && shouldSkipPianoExecution === true) return false;

  // Decision still pending (or an unknown state) — fail-closed hold.
  return undefined;
};
